import React, { useEffect, useRef, useState } from "react";
import "./Login.scss";
import FormInputField from "../../components/UserFormComponents/FormInputField/FormInputField";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import CustomButton from "../../components/CustomButton/CustomButton";
import { login, loginWithGoogle } from "../../api/auth";
import { useDispatch, useSelector } from "react-redux";
import { updateUser, updateUserToken } from "../../redux/features/user-slice";
import { toast } from "react-toastify";
import ROUTES from "../../constants/routes";

import { useGoogleLogin } from "@react-oauth/google";
import DesktopLoginContents from "../../components/DesktopLoginContents/DesktopLoginContents";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";
import { Capacitor } from "@capacitor/core";
import Captcha from "../../components/Captcha/Captcha";
import { storage } from "../../helpers/storage";

const Login = ({ setLoadingUser }) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const user = useSelector((state) => state.userReducer.value);

  const navigate = useNavigate();
  let gotoPath = null;

  const getGotoPath = async () => {
    gotoPath = (await storage.get("gotoPath")) || null;
  };

  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);
  const reRef = useRef(null);

  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    getGotoPath();
    if (user.token) {
      if (gotoPath) {
        navigate(gotoPath);
      } else {
        navigate(ROUTES.home.path);
      }
    }

    if (localStorage.getItem("email")) {
      let email = `${localStorage.getItem("email")}`;

      reset({ email: email });
    }
  }, []);

  const handleLogin = async (data) => {
    setSubmitting(true);
    setLoadingUser(true);
    let captchaToken = "";
    let source = "";
    if (Capacitor.isNativePlatform()) {
      source = "mobile";
    } else {
      captchaToken = await reRef.current.executeAsync();
    }
    const credentials = {
      email: data?.email,
      password: data?.password,
      remember_me: data?.remember_me,
      captchaToken: captchaToken,
      source: source,
    };
    login(credentials)
      .then((res) => {
        storage.remove("cart");
        if (res.data?.success) {
          if (res.data.user) {
            dispatch(updateUser(res.data.user));
          }

          if (rememberMe && res.data?.user?.email) {
            localStorage.setItem("email", res.data.user.email);
          } else {
            localStorage.removeItem("email");
          }

          if (res.data?.expires_at) {
            storage.set("expiresAt", res.data.expires_at);
          }
          if (res.data.token) {
            storage.set("userData", JSON.stringify(res.data.user));

            const tokenData = {
              token: res.data.token,
            };
            dispatch(updateUserToken(tokenData));
            storage.set("authToken", res.data.token);

            if (gotoPath) {
              navigate(gotoPath);
            } else {
              navigate(ROUTES.home.path);
            }
          }
        } else {
          if (res.data?.send_otp) {
            toast.dismiss();
            toast.info(res.data?.message);
            navigate(ROUTES.verifyOTP.path, {
              state: {
                userVerify: true,
                email: credentials.email,
                phone: null,
                resendTimer: res.data?.resend_timer || 60,
                gotoPath: gotoPath,
              },
            });
          } else {
            toast.dismiss();
            toast.error("Login failed!");
          }
        }
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          if (err?.response?.data?.errors) {
            if (err?.response?.data?.errors?.email) {
              toast.dismiss();
              toast.error(`${err?.response?.data?.errors?.email}`);
            } else {
              toast.dismiss();
              toast.error(`${err?.response?.data?.message}`);
            }
          } else {
            toast.dismiss();
            toast.error(`${err?.response?.data?.message}`);
          }
        } else {
          toast.dismiss();
          toast.error("Login failed!");
        }
      })
      .finally(() => {
        setSubmitting(false);
        setLoadingUser(false);
      });
  };

  const apiGoogleLogin = (credentials) => {
    setLoadingUser(true);
    const submitData = {
      ...credentials,
      remember_me: rememberMe,
    };
    loginWithGoogle(submitData)
      .then((res) => {
        storage.remove("cart");
        if (res.data.user) {
          storage.set("userData", JSON.stringify(res.data.user));
          dispatch(updateUser(res.data.user));
        }
        if (rememberMe && res.data?.user?.email) {
          localStorage.setItem("email", res.data.user.email);
        } else {
          localStorage.removeItem("email");
        }
        if (res.data?.expires_at) {
          storage.set("expiresAt", res.data.expires_at);
        }
        if (res.data.token) {
          const tokenData = {
            token: res.data.token,
          };
          dispatch(updateUserToken(tokenData));
          storage.set("authToken", res.data.token);

          if (gotoPath) {
            navigate(gotoPath);
          } else {
            navigate(ROUTES.home.path);
          }
        }
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          toast.dismiss();
          toast.error(`${err?.response?.data?.message}`);
        } else {
          toast.dismiss();
          toast.error("Google login failed!");
        }
      })
      .finally(() => {
        setSubmitting(false);
        setLoadingUser(false);
      });
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      GoogleAuth.initialize()
        .then((res) => {
          console.log("Google auth for app initialized", res);
        })
        .catch((err) => {
          console.log("Google auth for app initialization error", err);
        });
    }
  }, []);

  const handleGoogleLogin = () => {
    if (Capacitor.isNativePlatform()) {
      googleAppLogin();
    } else {
      googleWebLogin();
    }
  };

  const googleAppLogin = () => {
    GoogleAuth.signIn()
      .then((result) => {
        if (result.authentication?.accessToken) {
          setSubmitting(true);
          const credentials = {
            access_token: result.authentication?.accessToken,
            source: "mobile",
          };
          apiGoogleLogin(credentials);
        }
      })
      .catch((err) => {
        toast.dismiss();
        toast.error("App - Google Login failed!");
      });
  };

  const googleWebLogin = useGoogleLogin({
    onSuccess: async (response) => {
      if (response?.access_token) {
        setSubmitting(true);
        const captchaToken = await reRef.current.executeAsync();
        const credentials = {
          access_token: response.access_token,
          captchaToken: captchaToken,
        };
        apiGoogleLogin(credentials);
      }
    },
    onError: (error) => {
      toast.dismiss();
      toast.error("Google login failed!");
    },
  });
  return (
    <>
      <Captcha reRef={reRef} />
      <div className="initial-page-contents">
        <div className="login-page">
          <div className="form-container">
            <div className="heading">
              <p className="title">
                Welcome <br />
                <span>back!</span>
              </p>
              <p className="message">
                Sign in to access to your product details and get real-time
                updates on your products.
              </p>
            </div>
            <form onSubmit={handleSubmit(handleLogin)}>
              <div className="inputs">
                <FormInputField
                  name={"email"}
                  label={"Enter your mail/phone number"}
                  type={"email"}
                  iconType={"email"}
                  register={register}
                  watch={watch}
                  required={true}
                  inputErrors={errors}
                />
                <FormInputField
                  name={"password"}
                  label={"Enter your password"}
                  type={"password"}
                  iconType={"password"}
                  required={true}
                  watch={watch}
                  register={register}
                  inputErrors={errors}
                />
              </div>

              <div className="forgot-container">
                <div className="remember-me">
                  <label>
                    <input
                      defaultChecked={rememberMe}
                      type="checkbox"
                      {...register("remember_me")}
                      onChange={() => {
                        setRememberMe((pre) => !pre);
                      }}
                    />
                    <span className="checkbox-container"></span>
                  </label>
                  <p className="label">Remember me</p>
                </div>
                <Link to={ROUTES.forgotPassword.path}>Forgot Password</Link>
              </div>

              <CustomButton
                name={"Sign In"}
                type={"submit"}
                color={"white"}
                bigButton={true}
                disabled={submitting}
                handleSubmit={() => {}}
                loader={submitting}
              />

              <div className="hr-container">
                <span></span>
                <p>Or</p>
              </div>

              <button
                disabled={submitting}
                className="google-button"
                type="button"
                onClick={handleGoogleLogin}
              >
                <img
                  src="/images/google.svg"
                  alt=""
                  aria-label="Google Login"
                />
                Continue with google
              </button>

              <div className="register-link">
                <p>Don't have an account?</p>
                <Link to={ROUTES.register.path}>Create an account</Link>
              </div>
            </form>

            <p className="company-tag">
              &copy; Daikofin <span></span>
              <Link to={ROUTES.contactDUS.path}>Contact</Link>
              <span></span>
              <a href={"https://daikoapp.com/index.php/73-2/"} target="blank">
                Privacy & terms
              </a>
            </p>
          </div>
        </div>
        <DesktopLoginContents />
      </div>
    </>
  );
};

export default Login;
